import { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';

const DISMISS_KEY = 'rm_install_dismissed';
const DISMISS_DAYS = 7;

export default function InstallPromptBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Don't show if already running as installed PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return; // iOS Safari standalone

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const daysSince = (Date.now() - parseInt(dismissed)) / 86400000;
        if (daysSince < DISMISS_DAYS) return;
      }
      // Show banner after 6 seconds
      setTimeout(() => setShow(true), 6000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShow(false);
    setDeferredPrompt(null);
    setInstalling(false);
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!show || !deferredPrompt) return null;

  return (
    <div
      className="fixed bottom-20 left-3 right-3 z-[60] md:left-auto md:right-5 md:bottom-5 md:w-80 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="install-banner"
    >
      <div className="bg-slate-900 rounded-2xl shadow-2xl shadow-black/30 border border-slate-700 p-4 flex items-center gap-3">
        {/* App icon */}
        <div className="w-12 h-12 bg-blue-700 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <img src="/icon-192x192.png" alt="Rent Mitra" className="w-10 h-10 rounded-xl" onError={e => { e.target.style.display='none'; }} />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm leading-tight">Install Rent Mitra</p>
          <p className="text-slate-400 text-xs mt-0.5">Add to home screen for quick access</p>
        </div>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0 p-1"
          data-testid="install-banner-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Install button (full width below) */}
      <button
        onClick={handleInstall}
        disabled={installing}
        className="mt-2 w-full py-3 bg-blue-700 hover:bg-blue-600 disabled:opacity-60 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-900/40 transition-all active:scale-95"
        data-testid="install-banner-btn"
      >
        <Download className="w-4 h-4" />
        {installing ? 'Installing…' : 'Install App — Free'}
      </button>
    </div>
  );
}
